/* ====================================================================
   CARD ACTIONS + PRODUCT-PAGE STICKY CTA

   Cards get one of two treatments:

     * plain product  -> "Personalize" (straight into the Mediaclip
       designer) + "View details".
     * family member  -> NO hover buttons at all, just a persistent badge
       reading e.g. "18 options". These products come in sizes, colors or
       models that are separate Shopify products, so there is nothing to
       personalize until the customer has picked one -- opening the designer
       from the card would silently choose for them, and offering a button
       at all implies the card is the whole product. Clicking the card goes
       to the product page, which is where the selector lives. Membership
       and option counts come from pp-family-members.js, generated from the
       same plan as snippets/pp-options.liquid.

   How the designer is reached (measured, not guessed): Aurora's product
   form is a normal <form action="/cart/add">, and Mediaclip's button is a
   submit button that overrides the target --

     <button type="submit"
             formaction="/tools/customize/create-project"
             formmethod="post" data-is-customize-button="true">

   so personalizing is that form POSTed to the app-proxy route with
   `form_type`, `utf8`, `id` (variant), `product-id` and `quantity`. Posted
   from a COLLECTION page it redirects to
   `/tools/customize?projectId=<uuid>` -- verified live. Mediaclip's own JS
   API is not used; `getMediaclipAssociations()` currently 500s.
   ==================================================================== */
(function () {
  'use strict';

  var ENDPOINT = '/tools/customize/create-project';
  var CARD_SEL = 'product-card, .product-card';
  var cache = {};
  var prefetched = {};

  function familySize(handle) {
    return (window.PP_FAMILY_MEMBERS && window.PP_FAMILY_MEMBERS[handle]) || 0;
  }

  /* ⚠ `form.submit()` does NOT fire a submit event, so the progress bar in
     pp-enhance.js never saw the slowest navigation on the site. */
  function announceNavigation() {
    try { window.dispatchEvent(new CustomEvent('pp:navigating')); } catch (e) {}
  }

  function handleOf(card) {
    var a = card.querySelector('a[href*="/products/"]');
    if (!a) return null;
    var m = (a.getAttribute('href') || '').match(/\/products\/([^/?#]+)/);
    return m ? m[1] : null;
  }

  /* Styles live here rather than in pp-options.css so the badge cannot get
     out of sync with the markup that creates it. Positioned exactly like
     .pp-card-actions -- ⚠ do NOT add `position:relative` to
     .product-card__images-wrapper, that collapses every card image to zero
     height (learned the hard way). */
  function ensureBadgeCss() {
    if (document.getElementById('pp-optbadge-css')) return;
    var s = document.createElement('style');
    s.id = 'pp-optbadge-css';
    s.textContent =
      '.pp-optbadge{position:absolute;inset-block-end:10px;inset-inline-start:10px;' +
      'z-index:2;pointer-events:none;display:inline-flex;align-items:center;gap:5px;' +
      'padding:5px 10px;border-radius:999px;font-size:1.1rem;line-height:1;' +
      'font-weight:600;letter-spacing:.01em;color:#203B71;' +
      'background:rgba(255,255,255,.92);box-shadow:0 1px 4px rgba(32,59,113,.18);' +
      '-webkit-backdrop-filter:saturate(160%) blur(6px);' +
      'backdrop-filter:saturate(160%) blur(6px)}' +
      '.pp-optbadge::before{content:"";width:7px;height:7px;border-radius:50%;' +
      'background:currentColor;opacity:.55}';
    document.head.appendChild(s);
  }

  function prefetch(handle) {
    if (!handle) return Promise.resolve(null);
    if (!cache[handle]) {
      cache[handle] = fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; });
    }
    return cache[handle];
  }

  function prefetchPage(handle) {
    if (!handle || prefetched[handle]) return;
    prefetched[handle] = 1;
    try {
      var l = document.createElement('link');
      l.rel = 'prefetch'; l.href = '/products/' + handle; l.as = 'document';
      document.head.appendChild(l);
    } catch (e) {}
  }

  function openDesigner(product) {
    var form = document.createElement('form');
    form.method = 'post';
    form.action = ENDPOINT;
    form.style.display = 'none';
    [['form_type', 'product'], ['utf8', '✓'], ['id', product.variants[0].id],
     ['product-id', product.id], ['quantity', '1']].forEach(function (kv) {
      var i = document.createElement('input');
      i.type = 'hidden'; i.name = kv[0]; i.value = kv[1];
      form.appendChild(i);
    });
    document.body.appendChild(form);
    announceNavigation();
    form.submit();
  }

  function decorate(card) {
    if (!card || card.getAttribute('data-pp-cta')) return;
    var wrap = card.querySelector('.product-card__images-wrapper');
    var handle = handleOf(card);
    if (!wrap || !handle) return;
    card.setAttribute('data-pp-cta', '1');

    var titleEl = card.querySelector('.product-card__title');
    var title = titleEl ? titleEl.textContent.trim() : '';

    var warm = function () { prefetch(handle); prefetchPage(handle); };
    card.addEventListener('pointerenter', warm, { once: true, passive: true });

    var n = familySize(handle);
    if (n) {
      ensureBadgeCss();
      var badge = document.createElement('span');
      badge.className = 'pp-optbadge';
      badge.textContent = n > 1 ? n + ' options' : 'Options available';
      wrap.appendChild(badge);
      if (titleEl) {
        var sr = document.createElement('span');
        sr.className = 'visually-hidden';
        sr.textContent = ' — ' + (n > 1 ? n + ' options available' : 'options available');
        titleEl.appendChild(sr);
      }
      return;
    }

    var actions = document.createElement('div');
    actions.className = 'pp-card-actions';

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'pp-card-cta';
    btn.textContent = 'Personalize';
    btn.setAttribute('aria-label', title ? 'Personalize ' + title : 'Personalize this item');

    var details = document.createElement('a');
    details.className = 'pp-card-details';
    details.href = '/products/' + handle;
    details.textContent = 'View details';
    details.setAttribute('aria-label', title ? 'View details for ' + title : 'View product details');

    actions.appendChild(btn);
    actions.appendChild(details);
    wrap.appendChild(actions);

    btn.addEventListener('focus', warm, { once: true });
    details.addEventListener('focus', warm, { once: true });
    details.addEventListener('click', function (e) { e.stopPropagation(); });

    btn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      if (btn.getAttribute('data-busy')) return;
      btn.setAttribute('data-busy', '1');
      btn.classList.add('is-busy');
      btn.textContent = 'Opening…';
      prefetch(handle).then(function (p) {
        if (p && p.available && p.variants && p.variants.length === 1) {
          openDesigner(p);
        } else {
          announceNavigation();
          window.location.href = '/products/' + handle;
        }
      });
    });
  }

  /* ---- one card per family in search results -----------------------
     Jeff: "if i search for canvas, i get a wall of individual results for
     each size. and each thumbnail says '28 options' on it. its painfully
     redundant."

     Every size is its own product and has to stay that way (each carries its
     own Mediaclip template), and Shopify has no per-product "exclude from
     search". Collections already solve this by tagging only one member, but
     search indexes every published product, so it has to be collapsed here.

     ⛔ SEARCH ONLY. Collection grids are already deduplicated by tag, and a
     carousel or a recently-viewed row showing the size you actually looked at
     is correct, not redundant. */
  var ON_SEARCH = location.pathname.indexOf('/search') === 0;
  var GRID_ITEM = '.shop__grid-item';

  function primaryOf(handle) {
    return (window.PP_FAMILY_PRIMARY && window.PP_FAMILY_PRIMARY[handle]) || null;
  }

  /* ---- ask Shopify for one card per family, not 28 -----------------
     Collapsing in JS gets the cards right but not the page: the heading
     counted 33 while 2 showed, and page 2 came back nearly empty, because
     Shopify had still matched and paginated all 33. Its search DOES honour
     negation (measured: `canvas -tag:mediaclip` returns 0), so the 266
     non-primary family members carry `pp-family-alt` and every search from
     the store's own box excludes it. Counts and pagination are then right at
     the source, and the collapse below stays as the fallback for a link
     someone pasted without the filter.

     ⛔ THIS IS ONLY SAFE BECAUSE THE PRIMARY MATCHES THE WHOLE FAMILY.
     Each primary's description now ends with "Available in 28 sizes: 8x8,
     8x10, ..." (or 6 colors, or 3 set sizes). Without that, excluding
     siblings means a search for "20x60" drops the only product that matches
     it and answers a real query with nothing. Never ship the tag filter
     without that line.

     ⚠ Navigate rather than rewriting the input: leaving the filter in the box
     shows it to the shopper and it comes back on a Back press. The section
     also strips it from what it displays (search.liquid, shown_terms). */
  var FAMILY_FILTER = '-tag:pp-family-alt';

  function filterSearchSubmits() {
    var forms = document.querySelectorAll('form[action$="/search"], form[action*="/search?"]');
    for (var i = 0; i < forms.length; i++) {
      if (forms[i].getAttribute('data-pp-famfilter')) continue;
      forms[i].setAttribute('data-pp-famfilter', '1');
      forms[i].addEventListener('submit', function (e) {
        var input = this.querySelector('input[name="q"]');
        if (!input) return;
        var q = (input.value || '').trim();
        if (!q || q.indexOf(FAMILY_FILTER) > -1) return;
        if (e.defaultPrevented) return;          /* someone else owns this */
        e.preventDefault();
        var params;
        try {
          params = new URLSearchParams(new FormData(this));
        } catch (err) {
          params = new URLSearchParams();
        }
        params.set('q', q + ' ' + FAMILY_FILTER);
        announceNavigation();
        window.location.href =
          (this.getAttribute('action') || '/search').split('?')[0] + '?' + params.toString();
      });
    }
  }

  function collapseFamilies() {
    if (!ON_SEARCH) return;
    var items = document.querySelectorAll(GRID_ITEM);
    if (!items.length) return;
    var present = {}, rows = [], i;

    for (i = 0; i < items.length; i++) {
      var card = items[i].querySelector(CARD_SEL);
      var h = card && handleOf(card);
      if (!h) continue;
      present[h] = 1;
      rows.push([items[i], h]);
    }

    /* ⚠ NEVER LOSE A RESULT. The first version only hid a sibling when its
       family's PRIMARY was in the same results, and measured against the live
       page that hid nothing at all: search paginates at 16, so the 8x8 that
       represents the canvas family is usually not on the page you are looking
       at. Requiring the primary also breaks the opposite case — a search for
       "20x60" matches exactly one canvas, and dropping it would answer a real
       query with an empty page.
       So: keep ONE card per family per page. The primary if it is here,
       otherwise the first sibling that appeared. Every query still answers,
       and the survivor's own "28 options" badge leads to all the rest. */
    var keep = {};
    for (i = 0; i < rows.length; i++) {
      var fam = primaryOf(rows[i][1]);
      if (fam && present[fam]) keep[fam] = fam;
    }

    var shown = 0;
    for (i = 0; i < rows.length; i++) {
      var handle = rows[i][1], p = primaryOf(handle);
      if (!p) { shown++; continue; }                 /* not in any family */
      if (!keep[p]) keep[p] = handle;                /* first one wins */
      if (keep[p] === handle) { shown++; continue; }
      /* both, deliberately: [hidden] loses to the grid item's own display */
      rows[i][0].hidden = true;
      rows[i][0].style.display = 'none';
    }

    /* Both counters on this page report what Shopify matched, not what is on
       screen — and they are in different words, so leaving one behind put
       "2 Results" and "Products (33)" side by side. */
    if (!shown || shown === rows.length) return;

    var countEl = document.querySelector('.shop__product-count');
    if (countEl) {
      var txt = shown + (shown === 1 ? ' Result' : ' Results');
      if (countEl.textContent.trim() !== txt) countEl.textContent = txt;
    }
    /* the Products/Pages tab strip, which renders twice (a carousel copy) */
    var tabs = document.querySelectorAll('.search__tab-name');
    for (i = 0; i < tabs.length; i++) {
      var t = tabs[i].textContent.trim();
      var mm = t.match(/^(.*?)\s*\(\d+\)$/);
      if (mm) {
        var want = mm[1] + ' (' + shown + ')';
        if (t !== want) tabs[i].textContent = want;
      }
    }

    /* Shopify builds the <title> server side, so it still advertises the
       uncollapsed match count. Left alone the browser tab said "35 results"
       over a page reading "2 Results". */
    var dt = document.title.replace(/\d+ results? found/i, function (m) {
      return shown + (shown === 1 ? ' result found' : ' results found');
    });
    if (dt !== document.title) document.title = dt;
  }

  function scan() {
    var cards = document.querySelectorAll(CARD_SEL);
    for (var i = 0; i < cards.length; i++) decorate(cards[i]);
    collapseFamilies();
    filterSearchSubmits();
  }

  /* ---- sticky Personalize on the product page --------------------- */
  function stickyBar() {
    var real = document.querySelector('button[data-is-customize-button="true"]');
    if (!real || document.querySelector('.pp-sticky-cta')) return;

    var titleEl = document.querySelector('.product__title, h1');
    var bar = document.createElement('div');
    bar.className = 'pp-sticky-cta';
    bar.innerHTML =
      '<span class="pp-sticky-cta__title"></span>' +
      '<button type="button" class="btn btn--primary pp-sticky-cta__btn">Personalize this item</button>';
    bar.querySelector('.pp-sticky-cta__title').textContent = titleEl ? titleEl.textContent.trim() : '';
    document.body.appendChild(bar);

    bar.querySelector('.pp-sticky-cta__btn').addEventListener('click', function () {
      announceNavigation();
      real.click();
    });

    if (!('IntersectionObserver' in window)) return;
    new IntersectionObserver(function (entries) {
      bar.classList.toggle('is-visible', !entries[0].isIntersecting);
    }, { rootMargin: '-120px 0px 0px 0px' }).observe(real);
  }

  function watchRealButton() {
    var real = document.querySelector('button[data-is-customize-button="true"]');
    if (real) real.addEventListener('click', announceNavigation);
  }

  function init() { scan(); stickyBar(); watchRealButton(); }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  var queued = false;
  new MutationObserver(function () {
    if (queued) return;
    queued = true;
    requestAnimationFrame(function () { queued = false; scan(); });
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
